import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Search,
  Filter,
  Eye,
  Edit,
  FileText,
  ChevronRight
} from 'lucide-react';

interface PartRequest {
  id: string;
  created_at: string;
  brand: string;
  job_id: string | null;
  customer_first: string;
  customer_last: string;
  customer_phone: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  equipment_type: string;
  description: string;
  suspected_part_name: string;
  suspected_part_number: string;
  status: string;
  escalate_level: number;
  eta_date: string | null;
  vendor_name: string | null;
  po_number: string | null;
  tracking_number: string | null;
  last_update_at: string;
}

export default function PartsRequestsPage() {
  const [requests, setRequests] = useState<PartRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [escalationFilter, setEscalationFilter] = useState<string>('all');

  useEffect(() => {
    loadRequests();
    
    // Subscribe to real-time updates
    const subscription = supabase
      .channel('part_requests_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'part_requests' },
        () => {
          loadRequests();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  async function loadRequests() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('part_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error loading part requests:', error);
    } finally {
      setLoading(false);
    }
  }

  // Filter requests
  const filteredRequests = requests.filter(req => {
    // Status filter
    if (statusFilter !== 'all' && req.status !== statusFilter) return false;
    
    // Escalation filter
    if (escalationFilter === 'escalated' && req.escalate_level === 0) return false;
    if (escalationFilter === 'normal' && req.escalate_level > 0) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        req.customer_first?.toLowerCase().includes(search) ||
        req.customer_last?.toLowerCase().includes(search) ||
        req.suspected_part_name?.toLowerCase().includes(search) ||
        req.suspected_part_number?.toLowerCase().includes(search) ||
        req.job_id?.toLowerCase().includes(search) ||
        req.id.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      ordered: 'bg-yellow-100 text-yellow-800',
      received: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return styles[status] || styles.new;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <Clock className="w-4 h-4" />;
      case 'ordered':
        return <Package className="w-4 h-4" />;
      case 'received':
        return <CheckCircle className="w-4 h-4" />;
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getEscalationBadge = (level: number) => {
    if (level === 0) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">
        <AlertTriangle className="w-3 h-3" />
        Level {level}
      </span>
    );
  };

  // Stats
  const stats = {
    total: requests.length,
    new: requests.filter(r => r.status === 'new').length,
    ordered: requests.filter(r => r.status === 'ordered').length,
    escalated: requests.filter(r => r.escalate_level > 0).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading part requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parts Requests</h1>
          <p className="text-gray-600 mt-1">Manage technician part requests and orders</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <Package className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.new}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ordered</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.ordered}</p>
            </div>
            <Package className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Escalated</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.escalated}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by customer, part, or job ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="ordered">Ordered</option>
              <option value="received">Received</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Escalation Filter */}
          <div className="relative">
            <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={escalationFilter}
              onChange={(e) => setEscalationFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">All Requests</option>
              <option value="escalated">Escalated Only</option>
              <option value="normal">Normal Priority</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredRequests.length} {filteredRequests.length === 1 ? 'Request' : 'Requests'}
          </h2>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No part requests found</p>
            <p className="text-sm text-gray-500 mt-1">
              {searchTerm || statusFilter !== 'all' || escalationFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Requests will appear here when technicians submit them'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredRequests.map((request) => (
              <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {request.customer_first} {request.customer_last}
                      </h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${getStatusBadge(request.status)}`}>
                        {getStatusIcon(request.status)}
                        {request.status}
                      </span>
                      {getEscalationBadge(request.escalate_level)}
                    </div>

                    {/* Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span className="font-medium">Part:</span>
                        <span className="truncate">{request.suspected_part_name || 'Not specified'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="font-medium">Part #:</span>
                        <span>{request.suspected_part_number || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Address:</span>
                        <span className="truncate">{request.address1}, {request.city}, {request.state}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Equipment:</span>
                        <span>{request.equipment_type}</span>
                      </div>
                      {request.job_id && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Job ID:</span>
                          <span>{request.job_id}</span>
                        </div>
                      )}
                      {request.vendor_name && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Vendor:</span>
                          <span>{request.vendor_name}</span>
                        </div>
                      )}
                      {request.po_number && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">PO:</span>
                          <span>{request.po_number}</span>
                        </div>
                      )}
                      {request.eta_date && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">ETA:</span>
                          <span>{new Date(request.eta_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Timestamps */}
                    <div className="mt-2 text-xs text-gray-500">
                      Created {new Date(request.created_at).toLocaleString()} • 
                      Last updated {new Date(request.last_update_at).toLocaleString()}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/parts/${request.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
